<template>
  <div :class="attrs.class">
    <PrimaryInput
      v-bind="inputAttrs"
      :id="name"
      :name="name"
      :model-value="fieldValue"
      :required="isRequired"
      :valid="valid && passed"
      :error-message="errorMessage || fieldError"
      has-outside-label
      @update:model-value="onInput"
      @blur="emit('blur', passed)"
    >
      <template
        v-if="$slots['warning-message']"
        #warning-message
      >
        <slot name="warning-message" />
      </template>
    </PrimaryInput>
    <slot :passed="passed" />
  </div>
</template>

<script setup lang="ts">

import { useField } from 'vee-validate';

const props = withDefaults(defineProps<{
  modelValue?: string | number;
  rules?: string;
  errorMessage?: string;
  valid?: boolean;
  name?: string;
}>(), {
  modelValue: undefined,
  rules: 'required|min:3',
  errorMessage: '',
  valid: true,
  name: '',
});

const emit = defineEmits<{
  (e: 'blur', passed: boolean): void;
  (e: 'update:modelValue', value: string | number): void;
}>();

defineOptions({ name: 'ValidatedInput', inheritAttrs: false });

const attrs = useAttrs();

const inputAttrs = computed(() => {

  const { class: _class, style: _style, ...rest } = attrs as Record<string, unknown>;

  return rest;

});

// Single source of truth: the required star follows the vee-validate rules.
const isRequired = computed(() => props.rules
  .split('|')
  .some(rule => rule.split(':')[0] === 'required'));

const inputValue = computed(() => (props.modelValue ?? '') as string | number);

const fieldName = computed(() => props.name || 'field');

const { errorMessage: fieldError, meta, setValue } = useField<string | number>(
  fieldName,
  props.rules,
  {
    initialValue: inputValue.value,
    syncVModel: false,
  },
);

watch(inputValue, (newVal) => {

  setValue(newVal);

});

const passed = computed(() => meta.valid);

const onInput = (newValue: string | number) => {

  setValue(newValue);
  emit('update:modelValue', newValue);

};

const fieldValue = computed(() => inputValue.value);

</script>
